import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const isValidPhone = (phone) =>
  /^[6-9]\d{9}$/.test(phone.replace(/\s/g, ""));

const isValidName = (name) =>
  name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name.trim());

export default function Register() {
  const [form,    setForm]    = useState({ name:"", email:"", phone:"", password:"", role:"student" });
  const [errs,    setErrs]    = useState({});
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [teacherEmailSent, setTeacherEmailSent] = useState("");
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const { login } = useAuth();
  const nav = useNavigate();

  const isStudent = form.role === "student";

  const validate = () => {
    const e = {};

    if (!form.name.trim())
      e.name = "Name is required";
    else if (!isValidName(form.name))
      e.name = "Name should only contain letters (min 2 characters)";

    // Email only required for teachers
    if (!isStudent) {
      if (!form.email.trim())
        e.email = "Email is required";
      else if (!isValidEmail(form.email))
        e.email = "Enter a valid email address (e.g. rahul@gmail.com)";
    }

    if (!form.phone.trim())
      e.phone = "Phone number is required";
    else if (!isValidPhone(form.phone))
      e.phone = "Enter a valid 10 digit Indian mobile number (starts with 6-9)";

    if (!form.password)
      e.password = "Password is required";
    else if (form.password.length < 8)
      e.password = "Password must be at least 8 characters";
    else if (!/(?=.*[0-9])/.test(form.password))
      e.password = "Password must contain at least one number";

    return e;
  };

  const submit = async e => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrs(e2); return; }
    setLoading(true);
    try {
      const payload = {
        name:     form.name.trim(),
        phone:    form.phone.replace(/\s/g, ""),
        password: form.password,
        role:     form.role,
        ...(refCode ? { ref_code: refCode } : {}),
      };
      // Only send email for teachers
      if (!isStudent) payload.email = form.email.trim().toLowerCase();

      const { data } = await api.post("/auth/register", payload);

      if (isStudent) {
        // Students are auto-verified — log them in directly
        login(data.token, data.user);
        nav("/search");
      } else {
        // Teachers need email verification
        setTeacherEmailSent(form.email.trim().toLowerCase());
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Registration failed";
      if (msg.toLowerCase().includes("email"))
        setErrs({ email: msg });
      else if (msg.toLowerCase().includes("phone"))
        setErrs({ phone: msg });
      else
        setErrs({ general: msg });
    }
    setLoading(false);
  };

  const inp = (k) => ({
    width:"100%", marginTop:6, padding:"12px 14px", borderRadius:10,
    border:"1px solid " + (errs[k] ? "#ef4444" : "#1e293b"),
    background:"#0d0d0d", color:"#f1f5f9", fontSize:14,
    outline:"none", boxSizing:"border-box", transition:"border-color .2s",
    fontFamily:"inherit",
  });

  const strengthLevel = () => {
    const p = form.password;
    if (!p) return 0;
    let score = 0;
    if (p.length >= 6)  score++;
    if (p.length >= 10) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[^a-zA-Z0-9]/.test(p)) score++;
    return score;
  };

  const strength = strengthLevel();
  const strengthLabel = ["", "Weak", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#ef4444", "#ef4444", "#f59e0b", "#22c55e", "#22c55e"][strength];

  // Teacher post-register: show email verification prompt
  if (teacherEmailSent) {
    return (
      <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
        <div style={{ background:"#111111", borderRadius:16, border:"1px solid #1e293b", padding:"32px 24px", width:"100%", maxWidth:420, textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📧</div>
          <div style={{ fontSize:20, fontWeight:700, color:"#f1f5f9", marginBottom:10 }}>Check your email</div>
          <div style={{ fontSize:14, color:"#64748b", lineHeight:1.6, marginBottom:20 }}>
            We sent a verification link to<br />
            <span style={{ color:"#f97316", fontWeight:600 }}>{teacherEmailSent}</span>
          </div>
          <div style={{ fontSize:13, color:"#475569", background:"#0d0d0d", borderRadius:10, padding:"14px 16px", marginBottom:24, lineHeight:1.7 }}>
            Open the email and click <strong style={{ color:"#f1f5f9" }}>Verify Email</strong> to activate your account. The link expires in <strong style={{ color:"#f1f5f9" }}>24 hours</strong>.
          </div>
          <Link to="/login?role=teacher" style={{ display:"block", width:"100%", padding:"12px", background:"#f97316", color:"#fff", borderRadius:10, fontSize:15, fontWeight:700, textDecoration:"none", boxSizing:"border-box" }}>
            Go to Login →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
      <Helmet>
        <title>Register | PadhaiPass</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div style={{ background:"#111111", borderRadius:16, border:"1px solid #1e293b", padding:"28px 20px", width:"100%", maxWidth:420 }}>

        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}>
            <Logo size={28} />
          </div>
          <div style={{ fontSize:20, fontWeight:700, color:"#f1f5f9", marginBottom:4 }}>Create Account</div>
          <div style={{ fontSize:13, color:"#94a3b8" }}>Join PadhaiPass today</div>
        </div>

        <form onSubmit={submit} noValidate>

          {/* Name */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>Full Name *</label>
            <input
              style={inp("name")}
              value={form.name}
              onChange={e => { setForm(f => ({ ...f, name:e.target.value })); setErrs(v => ({ ...v, name:"" })); }}
              placeholder="Rahul Sharma"
              maxLength={50}
            />
            {errs.name && <div style={{ color:"#ef4444", fontSize:11, marginTop:4 }}>⚠ {errs.name}</div>}
          </div>

          {/* Role */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500, display:"block", marginBottom:10 }}>I am a:</label>
            <div style={{ display:"flex", gap:10 }}>
              {[
                { key:"student", label:"👨‍👩‍👧 Parent / Student" },
                { key:"teacher", label:"🧑‍🏫 Teacher" },
              ].map(r => (
                <button key={r.key} type="button" onClick={() => { setForm(f => ({ ...f, role:r.key })); setErrs({}); }}
                  style={{ flex:1, padding:"12px 8px", borderRadius:10, border:"2px solid "+(form.role===r.key?"#f97316":"#1e293b"), background:form.role===r.key?"#f9731610":"transparent", color:form.role===r.key?"#f97316":"#cbd5e1", fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .15s" }}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Email — teachers only */}
          {!isStudent && (
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>Email *</label>
              <input
                type="email"
                style={inp("email")}
                value={form.email}
                onChange={e => { setForm(f => ({ ...f, email:e.target.value })); setErrs(v => ({ ...v, email:"" })); }}
                placeholder="rahul@gmail.com"
                maxLength={100}
              />
              {errs.email
                ? <div style={{ color:"#ef4444", fontSize:11, marginTop:4 }}>⚠ {errs.email}</div>
                : form.email && isValidEmail(form.email)
                ? <div style={{ color:"#22c55e", fontSize:11, marginTop:4 }}>✓ Valid email</div>
                : null
              }
            </div>
          )}

          {/* Phone */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>
              WhatsApp Number * {isStudent && <span style={{ color:"#94a3b8", fontWeight:400 }}>(login ke liye bhi yahi use hoga)</span>}
            </label>
            <div style={{ position:"relative", marginTop:6 }}>
              <div style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#94a3b8", fontWeight:600 }}>+91</div>
              <input
                type="tel"
                style={{ ...inp("phone"), paddingLeft:44, marginTop:0 }}
                value={form.phone}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setForm(f => ({ ...f, phone: val }));
                  setErrs(v => ({ ...v, phone:"" }));
                }}
                placeholder="9876500000"
                maxLength={10}
              />
            </div>
            {errs.phone
              ? <div style={{ color:"#ef4444", fontSize:11, marginTop:4 }}>⚠ {errs.phone}</div>
              : form.phone.length === 10 && isValidPhone(form.phone)
              ? <div style={{ color:"#22c55e", fontSize:11, marginTop:4 }}>✓ Valid number</div>
              : form.phone.length > 0
              ? <div style={{ color:"#94a3b8", fontSize:11, marginTop:4 }}>{form.phone.length}/10 digits</div>
              : null
            }
          </div>

          {/* Password */}
          <div style={{ marginBottom:22 }}>
            <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>Password *</label>
            <div style={{ position:"relative", marginTop:6 }}>
              <input
                type={showPass ? "text" : "password"}
                style={{ ...inp("password"), paddingRight:44, marginTop:0 }}
                value={form.password}
                onChange={e => { setForm(f => ({ ...f, password:e.target.value })); setErrs(v => ({ ...v, password:"" })); }}
                placeholder="Min 8 characters with a number"
                maxLength={50}
              />
              <div onClick={() => setShowPass(s => !s)}
                style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", cursor:"pointer", fontSize:16, color:"#94a3b8", userSelect:"none" }}>
                {showPass ? "🙈" : "👁"}
              </div>
            </div>

            {form.password.length > 0 && (
              <div style={{ marginTop:8 }}>
                <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                  {[1,2,3,4,5].map(i => (
                    <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i <= strength ? strengthColor : "#1e293b", transition:"background .2s" }} />
                  ))}
                </div>
                <div style={{ fontSize:11, color: strengthColor }}>{strengthLabel} password</div>
              </div>
            )}

            {errs.password && <div style={{ color:"#ef4444", fontSize:11, marginTop:4 }}>⚠ {errs.password}</div>}
            <div style={{ fontSize:11, color:"#64748b", marginTop:6, lineHeight:1.6 }}>
              Must contain: 8+ characters, at least one number
            </div>
          </div>

          {errs.general && (
            <div style={{ color:"#ef4444", fontSize:13, marginBottom:14, background:"#ef444415", padding:"10px 14px", borderRadius:8, border:"1px solid #ef444430" }}>
              ⚠ {errs.general}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width:"100%", padding:13, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:loading?"not-allowed":"pointer", opacity:loading?0.7:1, transition:"opacity .2s" }}>
            {loading ? "Creating account..." : isStudent ? "Find Teachers →" : "Create Account →"}
          </button>
        </form>

        <p style={{ textAlign:"center", marginTop:18, fontSize:13, color:"#94a3b8" }}>
          Have account?{" "}
          <Link to="/login" style={{ color:"#f97316", fontWeight:600, textDecoration:"none" }}>Login</Link>
        </p>
      </div>
    </div>
  );
}
